import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";
import { ChevronLeft, ChevronRight, PanelLeft, Plus, Search, Settings, Star } from "lucide-react";
import { useConversations } from "@bastion/assistant";
import { handlePlainLinkClick } from "@bastion/ui";
import { useAuthStore } from "@/shared/stores/authStore";
import { visibleProductTabs, type ProductNavItem } from "../navigation";
import { useOrgRendering } from "../useOrgRendering";
import { ProfileMenuButton } from "./ProfileMenuButton";
import { useAppHistory } from "./useAppHistory";
import { shortcuts } from "./useAppShortcuts";
import { useArchivedChats } from "./useArchivedChats";
import { isDetailPage, sidebarParent, usePageVisits, type SidebarParent } from "./usePageVisits";
import { useStarredPages } from "./useStarredPages";
import { useStarredChats } from "./useStarredChats";
import styles from "./AppSidebar.module.css";

export interface AppSidebarProps {
  pathname: string;
  onToggle: () => void;
  onOpenSearch: () => void;
  onNavigate: (path: string) => void;
  inDrawer?: boolean;
}

/**
 * The sidebar's pages, in this order, as in the desktop app. Pages marked
 * `tucked` (navigation.ts) are not listed; search and their URLs still
 * reach them.
 */
const PAGE_ORDER = [
  "home-root",
  "/episodes",
  "/datasets",
  "/fleet/robots",
  "/ml-studio/policies",
  "/devservers",
];

/** The sidebar page each listed page is: it is current on its detail pages too. */
const PARENT_OF: Record<string, SidebarParent> = {
  "home-root": "home",
  "/episodes": "episodes",
  "/datasets": "datasets",
  "/fleet/robots": "robots",
  "/ml-studio/policies": "policies",
  "/devservers": "devservers",
};

/** Recent and Starred, under the pages with lists of things, as in the desktop app. */
const LISTS: Partial<Record<SidebarParent, { noun: string }>> = {
  episodes: { noun: "episodes" },
  datasets: { noun: "datasets" },
  robots: { noun: "robots and sites" },
  policies: { noun: "policies" },
};

/** Rows per Recent list. */
const RECENT_LIMIT = 5;

const RECENT_OPEN_KEY = "bastion.sidebar.recentOpen";

function readRecentOpen(parent: SidebarParent): boolean {
  try {
    return window.localStorage.getItem(`${RECENT_OPEN_KEY}:${parent}`) === "open";
  } catch {
    return false;
  }
}

function saveRecentOpen(parent: SidebarParent, open: boolean) {
  try {
    window.localStorage.setItem(`${RECENT_OPEN_KEY}:${parent}`, open ? "open" : "closed");
  } catch {
    // The choice lasts for this visit.
  }
}

function rank(item: ProductNavItem): number {
  const index = PAGE_ORDER.indexOf(item.id);
  return index === -1 ? PAGE_ORDER.length : index;
}

/** The chats under the pages: the starred ones, then the rest, newest first. */
function Chats({ pathname, onNavigate }: { pathname: string; onNavigate: (path: string) => void }) {
  const { data: conversations = [] } = useConversations();
  const { starredIdSet } = useStarredChats();
  const { archivedIdSet } = useArchivedChats();
  const newChatPath = `/chat/${crypto.randomUUID()}`;
  const shown = useMemo(() => {
    const visible = conversations
      .filter((c) => !archivedIdSet.has(c.session_id))
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    const starred = visible.filter((c) => starredIdSet.has(c.session_id));
    const rest = visible.filter((c) => !starredIdSet.has(c.session_id));
    return [...starred, ...rest];
  }, [conversations, archivedIdSet, starredIdSet]);

  return (
    <section aria-label="Chats">
      <header className={styles.groupHeader}>
        <span>Chats</span>
        <RouterLink
          to={newChatPath}
          onClick={(event) => handlePlainLinkClick(event, () => onNavigate(newChatPath))}
          className={styles.groupAction}
          aria-label="New chat"
          title="New chat"
        >
          <Plus className={styles.smallIcon} aria-hidden="true" />
        </RouterLink>
      </header>
      <ul className={styles.list}>
        {shown.map((c) => {
          const active = pathname === `/chat/${c.session_id}`;
          return (
            <li key={c.session_id}>
              <RouterLink
                to={`/chat/${c.session_id}`}
                onClick={(event) => handlePlainLinkClick(event, () => onNavigate(`/chat/${c.session_id}`))}
                className={styles.item}
                data-active={active ? "true" : undefined}
                aria-current={active ? "page" : undefined}
              >
                <span className={styles.label}>{c.title?.trim() || "Untitled chat"}</span>
                {starredIdSet.has(c.session_id) ? (
                  <Star className={styles.star} aria-label="Starred" />
                ) : null}
              </RouterLink>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

type Row = { path: string; label: string; context: string | null };

function PlaceRows({
  rows,
  pathname,
  onUnstar,
  onNavigate,
}: {
  rows: Row[];
  pathname: string;
  onUnstar?: (row: Row) => void;
  onNavigate: (path: string) => void;
}) {
  return (
    <ul className={styles.list}>
      {rows.map((row) => {
        const active = row.path === pathname;
        return (
          <li key={row.path} className={styles.placeRow}>
            <RouterLink
              to={row.path}
              onClick={(event) => handlePlainLinkClick(event, () => onNavigate(row.path))}
              className={styles.item}
              data-active={active ? "true" : undefined}
              aria-current={active ? "page" : undefined}
              title={[row.label, row.context].filter(Boolean).join(" · ")}
            >
              <span className={styles.label}>{row.label}</span>
              {row.context ? <span className={styles.detail}>{row.context}</span> : null}
            </RouterLink>
            {onUnstar ? (
              <button
                type="button"
                className={styles.unstar}
                onClick={() => onUnstar(row)}
                aria-label={`Unstar ${row.label}`}
                title="Unstar"
              >
                <Star className={styles.star} fill="currentColor" aria-hidden="true" />
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Recent and Starred things of the current page: episodes, robots, policies. */
function PlaceLists({ parent, pathname, onNavigate }: { parent: SidebarParent; pathname: string; onNavigate: (path: string) => void }) {
  const list = LISTS[parent]!;
  const visits = usePageVisits();
  const [open, setOpen] = useState(() => readRecentOpen(parent));
  const recent = visits
    .filter((visit) => isDetailPage(visit.path) && sidebarParent(visit.path) === parent)
    .slice(0, RECENT_LIMIT);

  return (
    <>
      {recent.length > 0 ? (
        <section aria-label={`Recent ${list.noun}`}>
          <button
            type="button"
            className={styles.groupHeader}
            aria-expanded={open}
            onClick={() => {
              saveRecentOpen(parent, !open);
              setOpen(!open);
            }}
          >
            <ChevronRight className={styles.disclosure} data-open={open ? "true" : undefined} aria-hidden="true" />
            <span>Recent {list.noun}</span>
            <span className={styles.groupCount}>{recent.length}</span>
          </button>
          {open ? <PlaceRows rows={recent} pathname={pathname} onNavigate={onNavigate} /> : null}
        </section>
      ) : null}
    </>
  );
}

/** Every route can be starred. Keep those links available from every page. */
function StarredPages({ pathname, onNavigate }: { pathname: string; onNavigate: (path: string) => void }) {
  const { starred, toggleStar } = useStarredPages();
  if (starred.length === 0) return null;
  return (
    <section className={styles.context} aria-label="Starred pages">
      <div className={styles.groupHeader}><span>Starred</span></div>
      <PlaceRows rows={starred} pathname={pathname} onUnstar={toggleStar} onNavigate={onNavigate} />
    </section>
  );
}

/**
 * The one sidebar, in the phone drawer or beside the page: back,
 * forward and search on top, one row per page, then what goes with the
 * current page: Chats on Home, and Recent and Starred on Episodes,
 * Datasets, Robots and Policies. You and Settings are at the bottom.
 */
export function AppSidebar({ pathname, onToggle, onOpenSearch, onNavigate, inDrawer = false }: AppSidebarProps) {
  const { back, forward, canGoBack, canGoForward } = useAppHistory();
  const me = useOrgRendering();
  const user = useAuthStore((state) => state.user);
  const tabs = visibleProductTabs(me);
  const pages = tabs
    .flatMap((tab) => tab.items)
    .filter((item) => !item.tucked)
    .sort((a, b) => rank(a) - rank(b));
  const parent = sidebarParent(pathname);

  return (
    <aside className={styles.sidebar} aria-label="Navigation" data-drawer={inDrawer ? "true" : undefined}>
      <div className={styles.top}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onToggle}
          aria-label={inDrawer ? "Close navigation" : "Hide sidebar"}
          title={inDrawer ? "Close navigation" : "Hide sidebar"}
        >
          <PanelLeft className={styles.icon} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={back}
          disabled={!canGoBack}
          aria-label="Go back"
          title={`Back (${shortcuts.back})`}
        >
          <ChevronLeft className={styles.icon} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={forward}
          disabled={!canGoForward}
          aria-label="Go forward"
          title={`Forward (${shortcuts.forward})`}
        >
          <ChevronRight className={styles.icon} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.searchButton}`}
          onClick={onOpenSearch}
          aria-label="Search"
          title={`Search (${shortcuts.commandPalette})`}
        >
          <Search className={styles.icon} aria-hidden="true" />
        </button>
      </div>

      <div className={styles.body}>
        <nav aria-label="Pages">
          <ul className={styles.list}>
            {pages.map((item) => {
              const Icon = item.icon;
              const active = PARENT_OF[item.id] !== undefined && PARENT_OF[item.id] === parent;
              return (
                <li key={item.id}>
                  <RouterLink
                    to={item.path}
                    onClick={(event) => handlePlainLinkClick(event, () => onNavigate(item.path))}
                    className={styles.item}
                    data-active={active ? "true" : undefined}
                    aria-current={active ? "page" : undefined}
                    data-testid={`sidebar-item-${item.id}`}
                  >
                    <Icon className={styles.icon} aria-hidden="true" />
                    <span className={styles.label}>{item.label}</span>
                  </RouterLink>
                </li>
              );
            })}
          </ul>
        </nav>
        <StarredPages pathname={pathname} onNavigate={onNavigate} />
        {parent === "home" ? (
          <div className={styles.context}>
            <Chats pathname={pathname} onNavigate={onNavigate} />
          </div>
        ) : parent && LISTS[parent] ? (
          <div className={styles.context}>
            <PlaceLists key={parent} parent={parent} pathname={pathname} onNavigate={onNavigate} />
          </div>
        ) : null}
      </div>

      <div className={styles.footer}>
        <div className={styles.person}>
          <ProfileMenuButton
            placement="top-start"
            triggerClassName={styles.profile}
            onNavigate={onNavigate}
          />
          <span className={styles.name}>{user?.name || user?.email || ""}</span>
        </div>
        <RouterLink
          to="/settings"
          onClick={(event) => handlePlainLinkClick(event, () => onNavigate("/settings"))}
          className={styles.item}
          data-active={pathname.startsWith("/settings") ? "true" : undefined}
          title={`Settings (${shortcuts.settings})`}
        >
          <Settings className={styles.icon} aria-hidden="true" />
          <span className={styles.label}>Settings</span>
          <kbd className={styles.kbd}>{shortcuts.settings}</kbd>
        </RouterLink>
      </div>
    </aside>
  );
}
